import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router';
import type { EdgeStatus, LiveValue } from './api/types.js';
import { useApi, useAuth } from './auth.js';
import { useResource } from './hooks.js';
import { ALARM_BADGE_REFRESH_MS, ALARMS_CHANGED_EVENT, alarmBadgeText } from './lib/alarms.js';
import {
  formatAge,
  formatLiveValue,
  formatTimestamp,
  qualityOf,
  statusFlagsOf,
  STATUS_FLAGS,
} from './lib/format.js';
import { navItems } from './lib/permissions.js';
import { unitSymbol } from './lib/units.js';

export function Layout() {
  const { user, logout } = useAuth();
  return (
    <div className="app">
      <header className="topbar">
        <NavLink to="/sites" className="brand">
          <span className="brand-mark" aria-hidden="true">
            K
          </span>
          Kalvio
        </NavLink>
        <nav className="nav">
          {user &&
            navItems(user.permissions).map((i) => (
              <NavLink key={i.to} to={i.to}>
                {i.label}
              </NavLink>
            ))}
          {user?.permissions.read && (
            <NavLink to="/alarms">
              Alarms
              <AlarmCounter />
            </NavLink>
          )}
        </nav>
        <div className="topbar-user">
          <span className="muted" title="Signed in">
            {user?.email}
          </span>
          <Link to="/settings/password" className="btn btn-ghost">
            Change password
          </Link>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              void logout();
            }}
          >
            Log out
          </button>
        </div>
      </header>
      <main className="main">
        <Outlet />
      </main>
    </div>
  );
}

/** Open alarms in the navigation: red while some are not acknowledged. */
function AlarmCounter() {
  const api = useApi();
  const counts = useResource(
    'alarm-counter',
    (signal) => api.alarms({ state: 'open', limit: 1 }, signal),
    ALARM_BADGE_REFRESH_MS,
  );
  const { reload } = counts;
  useEffect(() => {
    window.addEventListener(ALARMS_CHANGED_EVENT, reload);
    return () => {
      window.removeEventListener(ALARMS_CHANGED_EVENT, reload);
    };
  }, [reload]);
  const text = alarmBadgeText(counts.data?.open ?? 0);
  if (!text || !counts.data) return null;
  const unacked = counts.data.open_unacked > 0;
  return (
    <span
      className={`nav-count${unacked ? ' nav-count-alert' : ''}`}
      title={`${counts.data.open} open, ${counts.data.open_unacked} not acknowledged`}
    >
      {text}
    </span>
  );
}

export function StatusBadge({ status }: { status: EdgeStatus }) {
  const label = status === 'online' ? 'Online' : status === 'offline' ? 'Offline' : 'Unknown';
  return (
    <span className={`badge badge-${status}`}>
      <span className="dot" aria-hidden="true" />
      {label}
    </span>
  );
}

export function QualityBadge({ q }: { q: number | null | undefined }) {
  if (q === null || q === undefined) return <span className="muted">—</span>;
  const quality = qualityOf(q);
  return (
    <span className={`badge badge-q-${quality}`} title={`Quality ${q}`}>
      {quality}
    </span>
  );
}

export function StatusFlags({ sf }: { sf: number | null | undefined }) {
  const set = new Set(statusFlagsOf(sf).map((f) => f.key));
  if (set.size === 0) return <span className="flags flags-none" aria-label="No status flags" />;
  return (
    <span
      className="flags"
      aria-label={STATUS_FLAGS.filter((f) => set.has(f.key))
        .map((f) => f.label)
        .join(', ')}
    >
      {STATUS_FLAGS.filter((f) => set.has(f.key)).map((f) => (
        <abbr key={f.key} className={`flag flag-${f.key}`} title={f.label}>
          {f.letter}
        </abbr>
      ))}
    </span>
  );
}

/** A live value; `flashKey` changes when a pushed update changed it, which replays the flash. */
export function LiveValueText({
  dataType,
  unit,
  live,
  flashKey,
}: {
  dataType: string;
  unit: string;
  live: LiveValue | null | undefined;
  flashKey?: number | undefined;
}) {
  const text = formatLiveValue(dataType, unitSymbol(unit), live);
  return (
    <span key={flashKey ?? 0} className={flashKey ? 'value flash' : 'value'}>
      {text}
    </span>
  );
}

export function Age({
  ts,
  now,
  timeZone,
}: {
  ts: number | null | undefined;
  now: number;
  timeZone?: string;
}) {
  if (ts === null || ts === undefined) return <span className="muted">never</span>;
  const stale = now - ts > 15 * 60_000;
  return (
    <time
      dateTime={new Date(ts).toISOString()}
      title={formatTimestamp(ts, timeZone)}
      className={stale ? 'stale' : undefined}
    >
      {formatAge(ts, now)}
    </time>
  );
}

export function ErrorBox({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <div className="alert" role="alert">
      <span>{error.message}</span>
      {onRetry && (
        <button type="button" className="btn btn-small" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function Spinner({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label}
    </div>
  );
}

/**
 * Copies `text`; falls back to selecting `el` and the legacy copy command,
 * because `navigator.clipboard` needs HTTPS and the UI runs on plain HTTP in
 * the LAN.
 */
async function copyText(text: string, el: HTMLTextAreaElement | null): Promise<boolean> {
  try {
    if (window.isSecureContext && 'clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the selection.
  }
  if (!el) return false;
  el.focus();
  el.select();
  try {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the only copy path without HTTPS
    return document.execCommand('copy');
  } catch {
    return false;
  }
}

export function CopyBlock({ label, text }: { label: string; text: string }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [result, setResult] = useState<'copied' | 'failed' | null>(null);
  return (
    <div className="copy-block">
      <div className="copy-head">
        <span className="strong">{label}</span>
        <button
          type="button"
          className="btn btn-small"
          onClick={() => {
            void copyText(text, ref.current).then((ok) => {
              setResult(ok ? 'copied' : 'failed');
            });
          }}
        >
          {result === 'copied' ? 'Copied' : 'Copy'}
        </button>
      </div>
      <textarea
        ref={ref}
        className="copy-text mono"
        readOnly
        rows={Math.max(1, Math.ceil(text.length / 90))}
        value={text}
        aria-label={label}
        onFocus={(e) => {
          e.currentTarget.select();
        }}
      />
      {result === 'failed' && (
        <p className="form-error">Copying is blocked here; select the text and copy it yourself.</p>
      )}
    </div>
  );
}
